import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { essenceData } from '../data/translations';
import { mixColors } from '../utils/colorMixing';

const PerfumeContext = createContext(undefined);

const MAX_VOLUME = 50; // Maximum ml in the bottle

export const PerfumeProvider = ({ children }) => {
  // Formula: { essenceId: { drops: number, ml: number } }
  const [formula, setFormula] = useState({});
  const [animatingDrop, setAnimatingDrop] = useState(null);
  const [savedPerfumes, setSavedPerfumes] = useState(() => {
    const saved = localStorage.getItem('perfumeLab-savedPerfumes');
    return saved ? JSON.parse(saved) : [];
  });

  // Save to localStorage when savedPerfumes changes
  useEffect(() => {
    localStorage.setItem('perfumeLab-savedPerfumes', JSON.stringify(savedPerfumes));
  }, [savedPerfumes]);

  // Calculate total volume
  const totalVolume = Object.values(formula).reduce((sum, item) => sum + item.ml, 0);
  
  // Check if bottle is full
  const isBottleFull = totalVolume >= MAX_VOLUME;

  // Calculate fill percentage (0-100)
  const fillPercent = Math.min((totalVolume / MAX_VOLUME) * 100, 100);

  // Get mixed color based on current formula
  const getMixedColor = useCallback(() => {
    const entries = Object.entries(formula);
    if (entries.length === 0) return null;

    const colors = entries.map(([essenceId, data]) => {
      const essence = essenceData.find(e => e.id === essenceId);
      return {
        color: essence?.color || 'hsl(40, 60%, 70%)',
        weight: data.ml
      };
    });

    return mixColors(colors);
  }, [formula]);

  // Add a drop of essence
  const addDrop = useCallback((essenceId) => {
    if (isBottleFull) return;

    const essence = essenceData.find(e => e.id === essenceId);
    if (!essence) return;

    // Start drop animation
    setAnimatingDrop({
      essenceId,
      color: essence.color,
      id: Date.now()
    });

    // Update formula after animation delay
    setTimeout(() => {
      setFormula(prev => {
        const existing = prev[essenceId] || { drops: 0, ml: 0 };
        const newDrops = existing.drops + 1;
        const newMl = existing.ml + essence.mlPerDrop;
        
        return {
          ...prev,
          [essenceId]: {
            drops: newDrops,
            ml: Math.min(newMl, MAX_VOLUME - (totalVolume - existing.ml))
          }
        };
      });
      setAnimatingDrop(null);
    }, 800); // Match animation duration
  }, [isBottleFull, totalVolume]);

  // Clear the bottle (dump animation)
  const clearBottle = useCallback(() => {
    setFormula({});
  }, []);

  // Save current creation
  const saveCreation = useCallback((name) => {
    if (Object.keys(formula).length === 0) return;

    const newPerfume = {
      id: Date.now(),
      name,
      formula: { ...formula },
      totalVolume,
      mixedColor: getMixedColor(),
      createdAt: new Date().toISOString()
    };

    setSavedPerfumes(prev => [newPerfume, ...prev]);
    return newPerfume;
  }, [formula, totalVolume, getMixedColor]);

  // Load a saved perfume
  const loadPerfume = useCallback((perfume) => {
    setFormula(perfume.formula);
  }, []);

  // Delete a saved perfume
  const deletePerfume = useCallback((perfumeId) => {
    setSavedPerfumes(prev => prev.filter(p => p.id !== perfumeId));
  }, []);

  // Get formula as percentage array
  const getFormulaPercentages = useCallback(() => {
    if (totalVolume === 0) return [];
    
    return Object.entries(formula).map(([essenceId, data]) => ({
      essenceId,
      percentage: Math.round((data.ml / totalVolume) * 100),
      drops: data.drops,
      ml: data.ml
    }));
  }, [formula, totalVolume]);

  return (
    <PerfumeContext.Provider value={{
      formula,
      totalVolume,
      maxVolume: MAX_VOLUME,
      fillPercent,
      isBottleFull,
      animatingDrop,
      savedPerfumes,
      addDrop,
      clearBottle,
      saveCreation,
      loadPerfume,
      deletePerfume,
      getMixedColor,
      getFormulaPercentages
    }}>
      {children}
    </PerfumeContext.Provider>
  );
};

export const usePerfume = () => {
  const context = useContext(PerfumeContext);
  if (!context) {
    throw new Error('usePerfume must be used within a PerfumeProvider');
  }
  return context;
};
